/**
 * WebhookEventsCard (OCP-932) - payment-provider webhook events that need
 * attention: `failed` (the handler threw) or abandoned (`processing` past the
 * 10-minute window - the worker died). The billing.provider-webhook-sweep job
 * re-drives them every 5 minutes; "Redrive" does it now, after the cause is
 * fixed, with an audited reason - the same call as the
 * `ocp_redrive_webhook_event` MCP tool. The payload body is never shown.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError, readErrorKey } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface WebhookEventRow {
  id: string;
  provider: 'polar' | 'paddle';
  event_id: string;
  event_type: string;
  subscription_ref: string | null;
  status: string;
  abandoned: boolean;
  attempts: number;
  last_error: string | null;
  claimed_at: string;
  alerted_at: string | null;
  has_payload: boolean;
  redrivable: boolean;
}

interface RedriveResult {
  outcome: string;
  reason: string | null;
  error: string | null;
}

export function WebhookEventsCard() {
  const [items, setItems] = useState<WebhookEventRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ items: WebhookEventRow[]; attention: number }>('/v1/admin/billing/webhook-events?status=attention');
      setItems(r.items);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load webhook events (HTTP ${err.status}).` : 'Could not load webhook events.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const redrive = async (row: WebhookEventRow) => {
    if (reason.trim().length < 3) {
      setFlash('Give a reason (at least 3 characters) - it is audited.');
      return;
    }
    setBusy(true);
    setFlash(null);
    try {
      const r = await api<RedriveResult>(`/v1/admin/billing/webhook-events/${encodeURIComponent(row.id)}/redrive`, { method: 'POST', body: { reason: reason.trim() } });
      setFlash(r.outcome === 'failed' ? `Redrive of ${row.event_id} failed: ${r.error ?? 'unknown error'}` : `Redrive of ${row.event_id}: ${r.outcome}${r.reason ? ` (${r.reason})` : ''}`);
      setOpen(null);
      setReason('');
      await load();
    } catch (err) {
      setFlash(err instanceof ApiError ? `Redrive refused: ${readErrorKey(err.body) ?? `HTTP ${err.status}`}` : 'Redrive failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="webhook-events" className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 text-sm">
      <h2 className="font-semibold">Provider webhook events needing attention{items ? ` (${items.length})` : ''}</h2>
      <p className="mt-1 text-xs text-gray-500">
        Failed or abandoned Polar / Paddle events. The sweep retries them every 5 minutes; Redrive processes one now
        through the same handler and ordering rules (audited).
      </p>
      {error ? (
        <p role="alert" className="mt-2 text-red-700 dark:text-red-300">{error}</p>
      ) : items === null ? null : items.length === 0 ? (
        <p data-testid="webhook-events-empty" className="mt-2 text-gray-500">Nothing pending - every event was processed.</p>
      ) : (
        <table className="mt-2 w-full text-left text-xs">
          <thead>
            <tr className="text-gray-500">
              <th className="py-1">Provider</th>
              <th className="py-1">Event</th>
              <th className="py-1">State</th>
              <th className="py-1">Attempts</th>
              <th className="py-1">Last error</th>
              <th className="py-1">Claimed</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} data-testid={`webhook-event-${r.event_id}`} className="border-t border-gray-100 dark:border-gray-800 align-top">
                <td className="py-1">{r.provider}</td>
                <td className="py-1">
                  <div>{r.event_type}</div>
                  <div className="text-gray-500">{r.event_id}{r.subscription_ref ? ` / ${r.subscription_ref}` : ''}</div>
                </td>
                <td className="py-1">{r.abandoned ? 'abandoned' : r.status}{r.alerted_at ? ' (alerted)' : ''}</td>
                <td className="py-1">{r.attempts}</td>
                <td className="py-1 break-all">{r.last_error ?? '-'}</td>
                <td className="py-1">{fmtDateTime(r.claimed_at)}</td>
                <td className="py-1">
                  {!r.redrivable ? (
                    <span className="text-gray-500">{r.has_payload ? 'provider not wired' : 'no payload - resend from the provider'}</span>
                  ) : open === r.id ? (
                    <div className="space-y-1">
                      <label className="block">
                        <span className="sr-only">Redrive reason</span>
                        <input
                          aria-label="Redrive reason"
                          className="w-48 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-1 py-0.5"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Why now? (audited)"
                        />
                      </label>
                      <button type="button" className="underline" disabled={busy} onClick={() => void redrive(r)}>Confirm redrive</button>{' '}
                      <button type="button" className="underline text-gray-500" onClick={() => { setOpen(null); setReason(''); }}>Cancel</button>
                    </div>
                  ) : (
                    <button type="button" className="underline" onClick={() => { setOpen(r.id); setReason(''); setFlash(null); }}>Redrive</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {flash ? <p data-testid="webhook-events-flash" className="mt-2">{flash}</p> : null}
    </div>
  );
}
